import { useState, useEffect } from 'react'
import { supabase } from './supabase'

export default function InvitePlayers({ session, matchId, onDone, onBack }) {
  const [search, setSearch] = useState('')
  const [results, setResults] = useState([])
  const [invited, setInvited] = useState([])
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)

  const [friendIds, setFriendIds] = useState([])

  useEffect(() => {
    const loadFriends = async () => {
      const { data: f } = await supabase.from('friends').select('user_id, friend_id')
        .or(`user_id.eq.${session.user.id},friend_id.eq.${session.user.id}`)
        .eq('status', 'accepted')
      const ids = (f || []).map(r => r.user_id === session.user.id ? r.friend_id : r.user_id)
      setFriendIds(ids)
    }
    loadFriends()
  }, [session])

  const handleSearch = async (value) => {
    setSearch(value)
    if (value.length < 2) { setResults([]); return }
    setLoading(true)
    const { data } = await supabase
      .from('profiles')
      .select('id, username, first_name, last_name, phone')
      .or(
        'username.ilike.%' + value + '%,' +
        'first_name.ilike.%' + value + '%,' +
        'last_name.ilike.%' + value + '%,' +
        'phone.ilike.%' + value + '%'
      )
      .neq('id', session.user.id)
      .limit(20)
    const results = data || []
    const sorted = [
      ...results.filter(p => friendIds.includes(p.id)),
      ...results.filter(p => !friendIds.includes(p.id)),
    ]
    setResults(sorted.slice(0, 10))
    setLoading(false)
  }

  const toggleInvite = (player) => {
    setInvited(v =>
      v.find(p => p.id === player.id)
        ? v.filter(p => p.id !== player.id)
        : [...v, player]
    )
  }

  const handleDone = async () => {
    setSaving(true)
    setError(null)
    if (invited.length > 0) {
      const rows = invited.map(p => ({
        match_id: matchId,
        player_id: p.id,
        status: 'invited',
      }))
      const { error: inviteError } = await supabase
        .from('match_players')
        .upsert(rows, { onConflict: 'match_id,player_id', ignoreDuplicates: false })
      if (inviteError) {
        setSaving(false)
        return setError(inviteError.message)
      }
    }
    setSaving(false)
    onDone()
  }

  return (
    <div style={styles.screen}>

      <div style={styles.body}>
        <div style={styles.searchWrap}>
          <span style={styles.searchIcon}>⌕</span>
          <input
            style={styles.searchInput}
            placeholder="Search by name, username, phone..."
            value={search}
            onChange={e => handleSearch(e.target.value)}
          />
          {search.length > 0 && (
            <button onClick={() => { setSearch(''); setResults([]) }} style={styles.clearBtn}>✕</button>
          )}
        </div>

        {invited.length > 0 && (
          <div style={styles.invitedSection}>
            <div style={styles.sectionLabel}>INVITED ({invited.length})</div>
            <div style={styles.invitedList}>
              {invited.map(p => (
                <div key={p.id} style={styles.invitedChip}>
                  <div style={styles.chipAvatar}>{p.first_name.charAt(0).toUpperCase()}</div>
                  <span style={styles.chipName}>@{p.username}</span>
                  <button onClick={() => toggleInvite(p)} style={styles.chipRemove}>✕</button>
                </div>
              ))}
            </div>
          </div>
        )}

        {loading && <div style={styles.loadingText}>Searching...</div>}

        {!loading && results.length > 0 && (
          <div>
            <div style={styles.sectionLabel}>RESULTS</div>
            {results.map(p => {
              const isInvited = invited.find(x => x.id === p.id)
              return (
                <div key={p.id} style={styles.playerCard}>
                  <div style={styles.playerLeft}>
                    <div style={styles.avatar}>{p.first_name.charAt(0).toUpperCase()}</div>
                    <div>
                      <div style={styles.playerName}>{p.first_name} {p.last_name}</div>
                      <div style={styles.playerUsername}>@{p.username}</div>
                    </div>
                  </div>
                  <button
                    onClick={() => toggleInvite(p)}
                    style={{ ...styles.inviteBtn, ...(isInvited ? styles.inviteBtnActive : {}) }}>
                    {isInvited ? '✓ Invited' : '+ Invite'}
                  </button>
                </div>
              )
            })}
          </div>
        )}

        {!loading && search.length >= 2 && results.length === 0 && (
          <div style={styles.emptySearch}>
            <div style={styles.emptyIcon}>🔍</div>
            <div style={styles.emptyText}>No players found</div>
            <div style={styles.emptySubtext}>Try a different name, username, or phone number</div>
          </div>
        )}

        {search.length < 2 && invited.length === 0 && (
          <div style={styles.hint}>
            <div style={styles.hintIcon}>👥</div>
            <div style={styles.hintText}>Find players to invite</div>
            <div style={styles.hintSubtext}>Search by name, @username, or phone number</div>
          </div>
        )}

        {error && <div style={styles.error}>{error}</div>}
      </div>

      <div style={styles.footer}>
        <button
          style={{ ...styles.btn, opacity: saving ? 0.7 : 1 }}
          onClick={handleDone}
          disabled={saving}>
          {saving ? 'Sending...' : invited.length > 0 ? 'Send Invites & Go to Lobby →' : 'Skip — Go to Lobby →'}
        </button>
      </div>
    </div>
  )
}

const styles = {
  screen: { minHeight: '100vh', background: '#FDF9E7', display: 'flex', flexDirection: 'column', fontFamily: "'DM Sans', sans-serif", overflowX: 'hidden', width: '100%' },
  header: { background: '#0A0A0A', padding: '48px 24px 20px', display: 'flex', alignItems: 'center', gap: 16, flexShrink: 0 },
  backBtn: { background: 'none', border: 'none', color: '#C19A6B', fontSize: 24, cursor: 'pointer', padding: 0, lineHeight: 1 },
  headerTitle: { fontFamily: "'Outfit', sans-serif", fontWeight: 700, fontSize: 28, color: '#FDF9E7', letterSpacing: 2 },
  body: { flex: 1, padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 16, overflowY: 'auto' },
  searchWrap: { position: 'relative' },
  searchIcon: { position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', fontSize: 18, color: '#7A7570' },
  searchInput: { background: '#FFFFFF', border: '1.5px solid #D0CCC0', borderRadius: 12, padding: '13px 44px 13px 42px', fontSize: 15, color: '#1A1814', fontFamily: 'inherit', outline: 'none', width: '100%', boxSizing: 'border-box' },
  clearBtn: { position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: '#7A7570', fontSize: 14, cursor: 'pointer' },
  sectionLabel: { fontSize: 11, fontWeight: 700, color: '#7A7570', letterSpacing: 1.5, textTransform: 'uppercase', marginBottom: 10 },
  invitedSection: { display: 'flex', flexDirection: 'column', gap: 10 },
  invitedList: { display: 'flex', flexWrap: 'wrap', gap: 8 },
  invitedChip: { display: 'flex', alignItems: 'center', gap: 6, background: 'rgba(0,96,57,0.08)', border: '1.5px solid #00563F', borderRadius: 20, padding: '6px 12px' },
  chipAvatar: { width: 22, height: 22, borderRadius: 11, background: '#00563F', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700 },
  chipName: { fontSize: 13, color: '#00563F', fontWeight: 600 },
  chipRemove: { background: 'none', border: 'none', color: '#00563F', cursor: 'pointer', fontSize: 12, padding: 0, marginLeft: 2 },
  loadingText: { fontSize: 14, color: '#7A7570', textAlign: 'center', padding: '20px 0' },
  playerCard: { background: '#FFFFFF', borderRadius: 16, padding: '14px 16px', border: '1px solid #E0DDD4', display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 },
  playerLeft: { display: 'flex', alignItems: 'center', gap: 12 },
  avatar: { width: 42, height: 42, borderRadius: 21, background: 'rgba(0,96,57,0.08)', border: '2px solid #00563F', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, fontWeight: 700, color: '#00563F' },
  playerName: { fontSize: 14, fontWeight: 600, color: '#1A1814' },
  playerUsername: { fontSize: 12, color: '#7A7570', marginTop: 2 },
  inviteBtn: { background: 'transparent', border: '1.5px solid #D0CCC0', borderRadius: 10, padding: '8px 14px', fontSize: 13, color: '#7A7570', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' },
  inviteBtnActive: { background: 'rgba(0,96,57,0.08)', border: '1.5px solid #00563F', color: '#00563F' },
  emptySearch: { textAlign: 'center', padding: '32px 0' },
  emptyIcon: { fontSize: 32, marginBottom: 10 },
  emptyText: { fontSize: 15, fontWeight: 600, color: '#1A1814', marginBottom: 4 },
  emptySubtext: { fontSize: 13, color: '#7A7570' },
  hint: { textAlign: 'center', padding: '40px 0' },
  hintIcon: { fontSize: 40, marginBottom: 12 },
  hintText: { fontSize: 15, fontWeight: 600, color: '#1A1814', marginBottom: 4 },
  hintSubtext: { fontSize: 13, color: '#7A7570' },
  error: { background: 'rgba(192,57,43,0.08)', border: '1px solid rgba(192,57,43,0.25)', borderRadius: 12, padding: '12px 16px', fontSize: 14, color: '#C0392B' },
  footer: { padding: '16px 24px 32px', background: '#FDF9E7', borderTop: '1px solid #E0DDD4', flexShrink: 0 },
  btn: { background: 'linear-gradient(135deg,#00563F,#006B4D)', color: 'white', border: 'none', borderRadius: 14, padding: '16px 24px', fontSize: 15, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', letterSpacing: 0.3, width: '100%' },
}
